/**
 * AuditTrailPage — PAGE-009 (/audit)
 * Orchestrates filters, table, and FR-085 export button.
 * US-008 Steps 1-5. Authenticated roles: admin, reviewer, viewer, auditor.
 * WCAG 2.1 AA: skip link, main landmark, live regions.
 */
import { useState } from 'react';
import { ScrollText } from 'lucide-react';
import { AuditLogFilters, type AuditFilters } from './AuditLogFilters';
import { AuditLogTable } from './AuditLogTable';
import { AuditExportButton } from './AuditExportButton';
import { useQueryClient } from '@tanstack/react-query';
import { useAuditLogs, auditKeys } from '@/hooks/useAuditLogs';

const DEFAULT_FILTERS: AuditFilters = {
  eventTypes: [],
  dateFrom: '',
  dateTo: '',
  jobId: '',
  search: '',
};

export function AuditTrailPage() {
  const [filters, setFilters] = useState<AuditFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const qc = useQueryClient();

  const { data, isLoading, error } = useAuditLogs({ filters, page });

  const handleFiltersChange = (next: AuditFilters) => {
    setFilters(next);
    setPage(1); // reset to page 1 on filter change
  };

  const handleRetry = () => {
    void qc.invalidateQueries({ queryKey: auditKeys.all });
  };

  return (
    <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <ScrollText className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">Audit Trail</h1>
              <p className="text-sm text-muted-foreground">
                Searchable log of all pipeline events for your client.
              </p>
            </div>
          </div>

          {/* FR-085 Export — top-right of toolbar */}
          <AuditExportButton filters={filters} />
        </div>

        {/* Filters */}
        <AuditLogFilters
          filters={filters}
          onChange={handleFiltersChange}
        />

        {/* Table */}
        <AuditLogTable
          logs={data?.data}
          isLoading={isLoading}
          error={error instanceof Error ? error : null}
          onRetry={handleRetry}
          totalCount={data?.totalCount ?? 0}
          page={page}
          onPageChange={setPage}
        />
    </div>
  );
}
